import { AlertTriangle, ChevronDown, History, Info, Lock } from "lucide-react";
import { useState } from "react";
import { MetaText } from "@/components/plan-audit/AuditPrimitives";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { type GuideDetail, type GuideSection, useGuide } from "@/hooks/use-plan-audit";
import type { GuideBlockLabel } from "@/lib/enums";
import { formatDate } from "@/lib/plan-audit-format";

/**
 * The Solution Plan Guide — the standard every run scores against.
 *
 * Read-only, and labelled as such. This is the one screen where that gap is
 * worth stating on the page rather than only in a commit message: the design
 * presents it as an editor with a publish button, and a published Guide version
 * is the thing runs reference, so a half-working editor here could desynchronise
 * a score from the standard that produced it. The validation rules ARE
 * implemented (server-side, in src/api/guide.ts) and surfaced, so what blocks a
 * publish is visible even though publishing is not yet possible.
 */
export function GuideView() {
  const [version, setVersion] = useState<string | undefined>(undefined);
  const { data: guide, isLoading, isError, error, refetch } = useGuide(version);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [openBlock, setOpenBlock] = useState<GuideBlockLabel | null>("WHAT IT IS");
  const [historyOpen, setHistoryOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <Skeleton className="h-96 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }
  if (isError) return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  if (!guide) return <ErrorState message="No Guide version exists" />;

  const active = guide.sections.find((s) => s.id === activeSectionId) ?? guide.sections[0] ?? null;

  return (
    <>
      <ReadOnlyBanner guide={guide} />

      <div className="grid gap-4 lg:grid-cols-[260px_1fr] lg:items-start">
        <aside className="pa-card">
          <div className="pa-card-head">
            <h3>Sections</h3>
            <MetaText>{guide.sections.length}</MetaText>
          </div>
          <div className="pa-scroll flex flex-col gap-0.5 lg:max-h-[24rem]">
            {guide.sections.map((section) => (
              <button
                key={section.id}
                type="button"
                className="pa-guide-nav"
                data-active={section.id === active?.id}
                onClick={() => {
                  setActiveSectionId(section.id);
                  setOpenBlock("WHAT IT IS");
                }}
              >
                <span className="min-w-0 flex-1 truncate">{section.name}</span>
                <span
                  className="shrink-0 text-[10px]"
                  style={{
                    color: section.blockingReason === null ? "var(--fg-3)" : "var(--danger)",
                  }}
                >
                  {section.blockingReason === null ? section.group : "!"}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-4 border-t border-[var(--border-1)] pt-3">
            <button
              type="button"
              onClick={() => setHistoryOpen((o) => !o)}
              className="flex w-full items-center gap-2 border-0 bg-transparent text-left font-[inherit]"
              aria-expanded={historyOpen}
            >
              <History aria-hidden="true" size={14} className="text-[var(--fg-3)]" />
              <span className="flex-1 text-xs font-semibold text-[var(--fg-1)]">
                Version history
              </span>
              <ChevronDown
                aria-hidden="true"
                size={14}
                className="text-[var(--fg-3)] transition-transform"
                style={{ transform: historyOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </button>
            {historyOpen && (
              <div className="mt-2 flex flex-col gap-2">
                {guide.history.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => {
                      setVersion(entry.label);
                      setActiveSectionId(null);
                    }}
                    className="rounded-lg border border-[var(--border-1)] p-2 text-left font-[inherit]"
                    style={{
                      background: entry.label === guide.label ? "var(--bg-accent)" : "transparent",
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[11px] font-semibold text-[var(--fg-1)]">
                        {entry.label}
                      </span>
                      {/* Runs scored against this version. This is what makes
                          retiring or editing a published version consequential. */}
                      <MetaText>
                        {entry.runCount} run{entry.runCount === 1 ? "" : "s"}
                      </MetaText>
                    </div>
                    <p className="mt-0.5 text-[11px] leading-snug text-[var(--fg-2)]">
                      {entry.note}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {active === null ? (
          <div className="pa-card">
            <p className="text-[13px] text-[var(--fg-3)]">
              This Guide version has no sections recorded.
            </p>
          </div>
        ) : (
          <SectionEditor
            section={active}
            openBlock={openBlock}
            onToggleBlock={(label) => setOpenBlock((prev) => (prev === label ? null : label))}
          />
        )}
      </div>
    </>
  );
}

function ReadOnlyBanner({ guide }: { guide: GuideDetail }) {
  return (
    <div className="pa-card mb-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-lg font-bold text-[var(--brand-primary)]">
              Solution Plan Guide
            </h2>
            <span
              className="rounded-full px-2 py-0.5 font-mono text-[11px] font-semibold"
              style={{ background: "var(--bg-accent)", color: "var(--brand-accent-hover)" }}
            >
              {guide.label}
            </span>
            {guide.publishedAt !== null && (
              <MetaText>published {formatDate(guide.publishedAt)}</MetaText>
            )}
          </div>
          <p className="mt-1 max-w-[80ch] text-[13px] text-[var(--fg-2)]">
            Each block here is what a scorecard quotes back under “What the Guide asks for”. Editing
            one changes how every future plan is judged.
          </p>
        </div>

        <div
          className="flex max-w-md gap-2 rounded-lg border p-3"
          style={{ background: "var(--info-bg)", borderColor: "var(--info)" }}
        >
          <Lock
            aria-hidden="true"
            size={15}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--info)" }}
          />
          <div>
            <p className="text-xs font-semibold" style={{ color: "var(--info-fg)" }}>
              Read-only in this build
            </p>
            <p className="mt-0.5 text-xs leading-relaxed" style={{ color: "var(--info-fg)" }}>
              Editing and publishing are not wired up. A published version is what runs reference,
              so a partial editor could leave a score pointing at a standard that no longer says
              what it said. The validation rules are live and shown below.
            </p>
          </div>
        </div>
      </div>

      {guide.blockingSections.length > 0 && (
        <div
          className="mt-3 flex gap-2 rounded-lg border p-3"
          style={{ background: "var(--danger-bg)", borderColor: "var(--danger)" }}
        >
          <AlertTriangle
            aria-hidden="true"
            size={15}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--danger)" }}
          />
          <p className="text-xs leading-relaxed" style={{ color: "var(--danger-fg)" }}>
            <b>{guide.blockingSections.length} section</b>
            {guide.blockingSections.length === 1 ? "" : "s"} could not be published:{" "}
            {guide.blockingSections.join(", ")}.
          </p>
        </div>
      )}
    </div>
  );
}

function SectionEditor({
  section,
  openBlock,
  onToggleBlock,
}: {
  section: GuideSection;
  openBlock: GuideBlockLabel | null;
  onToggleBlock: (label: GuideBlockLabel) => void;
}) {
  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <div>
          <h2>{section.name}</h2>
          <MetaText>
            {section.group === "Unassigned" ? "No group assigned" : section.group} ·{" "}
            {section.weight.toFixed(1)}× weight
          </MetaText>
        </div>
      </div>

      {section.blockingReason !== null && (
        <div
          className="mb-4 flex gap-2 rounded-lg border p-3"
          style={{ background: "var(--danger-bg)", borderColor: "var(--danger)" }}
        >
          <AlertTriangle
            aria-hidden="true"
            size={15}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--danger)" }}
          />
          <p className="text-xs leading-relaxed" style={{ color: "var(--danger-fg)" }}>
            {section.blockingReason}
          </p>
        </div>
      )}

      {section.blocks.length === 0 ? (
        <p className="text-[13px] text-[var(--fg-3)]">This section has no blocks yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {section.blocks.map((block) => {
            const open = openBlock === block.label;
            return (
              <div
                key={block.id}
                className="overflow-hidden rounded-lg border"
                style={{
                  borderColor: block.emptyAndRequired ? "var(--danger)" : "var(--border-1)",
                }}
              >
                <button
                  type="button"
                  onClick={() => onToggleBlock(block.label)}
                  className="flex w-full items-center gap-2 border-0 bg-transparent px-3 py-2.5 text-left font-[inherit]"
                  aria-expanded={open}
                >
                  <span className="pa-eyebrow shrink-0">{block.label}</span>
                  {block.required && (
                    <span className="shrink-0 text-[9.5px] font-bold tracking-wider text-[var(--fg-3)] uppercase">
                      required
                    </span>
                  )}
                  {!open && (
                    <span
                      className="min-w-0 flex-1 truncate text-[12.5px]"
                      style={{
                        color: block.emptyAndRequired ? "var(--danger)" : "var(--fg-3)",
                      }}
                    >
                      {block.body.trim().length > 0
                        ? block.body
                        : block.emptyAndRequired
                          ? "Empty — required to score"
                          : "Not written — optional"}
                    </span>
                  )}
                  <ChevronDown
                    aria-hidden="true"
                    size={15}
                    className="ml-auto shrink-0 text-[var(--fg-3)] transition-transform"
                    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>
                {open && (
                  <div className="border-t border-[var(--border-1)] px-3 py-3">
                    {block.body.trim().length > 0 ? (
                      <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-[var(--fg-2)]">
                        {block.body}
                      </p>
                    ) : (
                      <p className="flex items-center gap-2 text-[13px] text-[var(--fg-3)] italic">
                        <Info aria-hidden="true" size={14} />
                        Nothing written for this block.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
